// File: 101 app.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

// --- RESTORED LOGO IMPORT ---
// UNCOMMENT THIS IN YOUR ACTUAL PRODUCTION ENVIRONMENT:
// import AccelLogo from './accel_logo.png';

// --- MOCK FOR PREVIEW ENVIRONMENT ---
// This prevents the compiler error in the preview canvas
const AccelLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='375' height='100'%3E%3Crect width='375' height='100' fill='%23333'/%3E%3Ctext x='187.5' y='50' fill='%23fff' font-family='sans-serif' font-size='20' dominant-baseline='middle' text-anchor='middle'%3EAccel Compression Logo%3C/text%3E%3C/svg%3E";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Inline mock for jwt-decode
const jwtDecode = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return {};
    }
};

// Inline mock for HistoryModal
const HistoryModal = ({ item, onClose }) => (
    <div className="modal-overlay">
        <div className="modal-content">
            <button className="modal-close-button" onClick={onClose}>X</button>
            <h2>History for {item.name}</h2>
            <p style={{color: 'white', marginTop: '20px'}}>Historical data graph placeholder.</p>
        </div>
    </div>
);

// Inline mock for AlarmPage
const AlarmPage = ({ setCurrentPage }) => (
    <div style={{ padding: '20px', color: 'white', textAlign: 'center' }}>
        <h2>Alarm Status Page Placeholder</h2>
        <button onClick={() => setCurrentPage('dashboard')} className="home-button">Back to Dashboard</button>
    </div>
);

const VFDPage = ({ setCurrentPage, vfdUrlWithToken }) => {
    return (
        <div className="vfd-page-container">
            <div className="vfd-header">
                <h2>VFD Controls Interface</h2>
                <button onClick={() => setCurrentPage('dashboard')} className="home-button">Back to Dashboard</button>
            </div>
            <iframe src={vfdUrlWithToken} title="VFD Controls" className="vfd-iframe" />
        </div>
    );
};

const CollapsibleSection = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false); 
    return (
        <div className="section-container">
            <button className="collapsible-button" onClick={() => setIsOpen(!isOpen)}>
                <h2>{title}</h2>
                <span className="collapsible-icon">{isOpen ? '▼' : '▶'}</span>
            </button>
            {isOpen && (
                <div className="collapsible-content">
                    {children}
                </div>
            )}
        </div>
    );
};

const createInitialChartState = (label, borderColor, stepped = false) => ({
    datasets: [{
        label, data: [], borderColor, backgroundColor: borderColor + '80', tension: 0.2, pointRadius: 1, stepped: stepped, spanGaps: true, 
    }]
});

function App() {
    const [plcData, setPlcData] = useState({ coils: {}, holdingRegisters: {} });
    const [error, setError] = useState(null);
    const [historyItem, setHistoryItem] = useState(null);
    const [registerInputs, setRegisterInputs] = useState({});
    const [userRoles, setUserRoles] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [alarmConfig, setAlarmConfig] = useState([]);
    const [alarmStatusMessage, setAlarmStatusMessage] = useState('Checking for alarms...');
    const [compressorStatus, setCompressorStatus] = useState('Checking...');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginError, setLoginError] = useState('');

    const API_GATEWAY_URL = 'https://api.thomasevolutioncontrols.com';
    const HOMEPAGE_URL = 'https://tehome.axiom-link.com'; 
    const VFD_URL = 'https://vfd-101.thomasevolutioncontrols.com';
    const DASHBOARD_ID = '101'; 
    
    const POLLING_INTERVAL = 1000;
    const MAX_DATA_POINTS = (6 * 60 * 60 * 1000) / POLLING_INTERVAL;

    const diConfig = useMemo(() => [
        { name: 'DI0',  label: 'Compressor Motor Run Confirmation',     address: 24576,  isReadOnly: false, type: 'coil' },
        { name: 'DI1',  label: 'Oil Motor Run Confirmation-Motor ',      address: 24577,  isReadOnly: false, type: 'coil' },
        { name: 'DI2',  label: 'VFD Fault -VFD',    address: 24578,  isReadOnly: true, type: 'coil' },
        { name: 'DI15', label: 'I15-Low Discharge Pressure', address: 24591, isReadOnly: true, type: 'coil' },
        { name: 'DI16', label: 'I16-High Discharge Pressure', address: 24592, isReadOnly: true, type: 'coil' },
        { name: 'DI17', label: 'I17-ON/OFF SWITCH', address: 24593, isReadOnly: true, type: 'coil' },
    ], []);
    
    const doConfig = useMemo(() => [
        { name: 'DO0',  label: 'watchdog timer',     address: 16384,  isReadOnly: false, type: 'coil' },
        { name: 'DO1',  label: 'Oil Cooler Start 24 VDC',     address: 16385,  isReadOnly: false, type: 'coil' },
        { name: 'DO2',  label: 'Compressor Start',      address: 16386,  isReadOnly: true, type: 'coil' },
        { name: 'DO7',  label: 'Alarm Output', address: 16391,  isReadOnly: true, type: 'coil' },
    ], []);
    
    const unitControlsConfig = useMemo(() => [
        { name: 'MB75',  label: 'ALARM RESET PULSE',  address: 75, isReadOnly: false, type: 'coil' },
        { name: 'MB190', label: 'REMOTE START BIT',  address: 190, isReadOnly: false, type: 'coil' },
        { name: 'MB191', label: 'REMOTE RUN BIT',  address: 191, isReadOnly: false, type: 'coil' }
    ], []);

    // --- ACCURATE 32-BIT GENCOMM MAPPINGS ---
    const aiConfig = useMemo(() => [
        // BUS Metrics
        { name: 'AI_1091', label: 'Bus Frequency',       address: 1091, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1098', label: 'Bus Volts L1-L2',     address: 1098, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1100', label: 'Bus Volts L2-L3',     address: 1100, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1102', label: 'Bus Volts L3-L1',     address: 1102, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1104', label: 'Bus Amps L1',         address: 1104, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1106', label: 'Bus Amps L2',         address: 1106, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1108', label: 'Bus Amps L3',         address: 1108, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1112', label: 'Bus kW L1',           address: 1112, isReadOnly: true, type: 'register', scale: 1000000 },
        { name: 'AI_1114', label: 'Bus kW L2',           address: 1114, isReadOnly: true, type: 'register', scale: 1000000 },
        { name: 'AI_1116', label: 'Bus kW L3',           address: 1116, isReadOnly: true, type: 'register', scale: 1000000 },
        
        // LOAD Metrics
        { name: 'AI_1204', label: 'Load Amps L1',        address: 1204, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1206', label: 'Load Amps L2',        address: 1206, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1208', label: 'Load Amps L3',        address: 1208, isReadOnly: true, type: 'register', scale: 10 },
        { name: 'AI_1210', label: 'Load kW L1',          address: 1210, isReadOnly: true, type: 'register', scale: 1000000 },
        { name: 'AI_1212', label: 'Load kW L2',          address: 1212, isReadOnly: true, type: 'register', scale: 1000000 },
        
        // System Status Metrics
        { name: 'AI_768',  label: 'Number of Active Alarms',address: 768,  isReadOnly: true, type: 'register', scale: 1 },
        { name: 'AI_772',  label: 'Controller Mode',       address: 772,  isReadOnly: true, type: 'register', scale: 1 }, 
    ], []);

    const aoConfig = useMemo(() => [
        { name: 'AO_4104', label: 'System Control Key',    address: 4104, isReadOnly: false, type: 'register', scale: 1 },
        { name: 'AO_4105', label: 'Remote Start Control',  address: 4105, isReadOnly: false, type: 'register', scale: 1 },
        { name: 'AO_4106', label: 'Alarm Reset Key',       address: 4106, isReadOnly: false, type: 'register', scale: 1 },
        { name: 'AO_4107', label: 'Load Control Key',      address: 4107, isReadOnly: false, type: 'register', scale: 1 },
    ], []);

    const chartColors = useMemo(() => [
        'rgb(54, 162, 235)', 'rgb(75, 192, 192)', 'rgb(255, 99, 132)',
        'rgb(255, 206, 86)', 'rgb(153, 102, 255)', 'rgb(255, 159, 64)'
    ], []);

    const [charts, setCharts] = useState(() => {
        const defaultSelections = aiConfig.slice(0, 6);
        return defaultSelections.map((selection, index) => ({
            selection: selection,
            data: createInitialChartState(selection.label, chartColors[index], selection.type === 'coil')
        }));
    });

    const chartSelectionsStr = useMemo(() => JSON.stringify(charts.map(chart => chart.selection)), [charts]);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('jwt_token');
        if (token) { return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }; }
        return null;
    }, []);

    const canWrite = () => userRoles.includes('admin');

    const loadChartSettings = useCallback(async () => {
        const headers = getAuthHeaders();
        if (!headers) return null;
        try {
            const response = await fetch(`${API_GATEWAY_URL}/api/settings/${DASHBOARD_ID}`, { headers });
            if (response.status === 404) return null;
            if (!response.ok) throw new Error(`Failed to load settings: ${response.statusText}`);
            const settings = await response.json();
            return (Array.isArray(settings) && settings.length === 6) ? settings : null;
        } catch (err) {
            console.error("Error loading chart settings:", err);
            return null;
        }
    }, [getAuthHeaders]);

    const saveChartSettings = useCallback(async (selectionsToSave) => {
        const headers = getAuthHeaders();
        if (!headers) return;
        try {
            await fetch(`${API_GATEWAY_URL}/api/settings/${DASHBOARD_ID}`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(selectionsToSave),
            });
        } catch (err) {
            console.error("Error saving chart settings:", err);
        }
    }, [getAuthHeaders]);

    const saveTimeoutRef = useRef(null);

    useEffect(() => {
        if (isInitialLoad) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveTimeoutRef.current = setTimeout(() => {
            saveChartSettings(JSON.parse(chartSelectionsStr));
        }, 2000);
        
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [chartSelectionsStr, saveChartSettings, isInitialLoad]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (token) {
            localStorage.setItem('jwt_token', token);
            try {
                const decodedToken = jwtDecode(token);
                setUserRoles(decodedToken.roles || []);
                setIsLoggedIn(true);
            } catch (e) {
                console.error("Invalid token:", e);
                setUserRoles([]);
            }
            window.history.replaceState({}, document.title, window.location.pathname);
        } else {
            const localToken = localStorage.getItem('jwt_token');
            if (localToken) {
                try {
                    const decodedToken = jwtDecode(localToken);
                    setUserRoles(decodedToken.roles || []);
                    setIsLoggedIn(true); 
                } catch (e) {
                    console.error("Invalid token:", e);
                    setUserRoles([]);
                }
            }
        }
    }, []);

    const fetchInitialChartData = useCallback(async (selection) => {
        const headers = getAuthHeaders();
        if (!headers) return [];
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - (6 * 60 * 60 * 1000));
        try {
            const response = await fetch(
                `${API_GATEWAY_URL}/api/history/${DASHBOARD_ID}?type=${selection.type}&address=${selection.address}&start=${startTime.toISOString()}&end=${endTime.toISOString()}`,
                { headers }
            );
            if (!response.ok) return [];
            const data = await response.json();
            
            const scale = selection.scale || 1;
            return data.map(d => ({ x: new Date(d.time).toLocaleTimeString(), y: d.value / scale })) || [];
        } catch (err) {
            console.error(`Error fetching initial chart data for ${selection.label}`, err);
            return [];
        }
    }, [getAuthHeaders]);

    const fetchData = useCallback(async () => {
        const headers = getAuthHeaders();
        if (!headers) {
            setError('Authentication token not found.');
            return;
        }
        try {
            if (alarmConfig.length === 0) {
                const configResponse = await fetch(`${API_GATEWAY_URL}/api/alarms/config/${DASHBOARD_ID}`, { headers });
                if (configResponse.ok) {
                    const config = await configResponse.json();
                    setAlarmConfig(config);
                }
            }
            const response = await fetch(`${API_GATEWAY_URL}/api/scada-data/${DASHBOARD_ID}`, { headers });
            
            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return; 
            }
            if (!response.ok) throw new Error(`Network error: ${response.statusText}`);
            
            const data = await response.json();
            setPlcData(data);
            setError(null);

            if (data.holdingRegisters) {
                const activeAlarms = data.holdingRegisters[768];
                if (activeAlarms && activeAlarms > 0) {
                    setAlarmStatusMessage(`🚨 Active Alarms: ${activeAlarms} 🚨`);
                } else {
                    setAlarmStatusMessage('✅ System OK: No Active Alarms.');
                }
            }

            if (data.holdingRegisters) {
                const statusValue = data.holdingRegisters[772];
                switch (statusValue) {
                    case 0: setCompressorStatus('Stop'); break;
                    case 1: setCompressorStatus('Auto'); break;
                    case 2: setCompressorStatus('Manual'); break;
                    default: setCompressorStatus('Unknown'); break;
                }
            }

            setCharts(prevCharts => {
                const currentTime = new Date().toLocaleTimeString();
                return prevCharts.map(chart => {
                    const { selection } = chart;
                    const scale = selection.scale || 1;
                    const newValue = selection.type === 'coil'
                        ? (data.coils ? data.coils[selection.address] : 0)
                        : (data.holdingRegisters ? (data.holdingRegisters[selection.address] / scale) : 0);
                        
                    const newDataPoint = { x: currentTime, y: newValue || 0 };
                    const currentData = chart.data.datasets[0].data || [];
                    const updatedData = [...currentData, newDataPoint].slice(-MAX_DATA_POINTS);
                    return { ...chart, data: { datasets: [{ ...chart.data.datasets[0], data: updatedData }] } };
                });
            });

        } catch (error) {
            setError(`Failed to fetch latest data: ${error.message}. Retrying...`);
        }
    }, [getAuthHeaders, MAX_DATA_POINTS, HOMEPAGE_URL, alarmConfig]);

    useEffect(() => {
        const initializeDashboard = async () => {
            const headers = getAuthHeaders();
            if (!headers) {
                setTimeout(initializeDashboard, 250);
                return;
            }
            
            const savedSelections = await loadChartSettings();
            
            let selectionsToUse = aiConfig.slice(0, 6);
            if (savedSelections && savedSelections.length === 6) {
                const allConfigs = [...aiConfig, ...aoConfig, ...diConfig, ...doConfig];
                selectionsToUse = savedSelections.map(savedSel => {
                    const matchingConfig = allConfigs.find(c => c.address === savedSel.address && c.type === savedSel.type);
                    return matchingConfig ? matchingConfig : savedSel;
                });
            }

            const initialCharts = selectionsToUse.map((selection, index) => ({
                selection: selection,
                data: createInitialChartState(selection.label, chartColors[index], selection.type === 'coil')
            }));
            setCharts(initialCharts);
            const initialDataPromises = initialCharts.map(chart => fetchInitialChartData(chart.selection));
            const allInitialData = await Promise.all(initialDataPromises);
            setCharts(prevCharts => {
                const updatedCharts = prevCharts.map((chart, index) => ({
                    ...chart,
                    data: { datasets: [{ ...chart.data.datasets[0], data: allInitialData[index] }] }
                }));
                setIsInitialLoad(false);
                return updatedCharts;
            });
        };
        if (isLoggedIn && isInitialLoad) {
            initializeDashboard();
        }
    }, [isLoggedIn, isInitialLoad, getAuthHeaders, loadChartSettings, fetchInitialChartData, chartColors, aiConfig, aoConfig, diConfig, doConfig]);

    useEffect(() => {
        if (isInitialLoad || !isLoggedIn) return;
        fetchData();
        const interval = setInterval(fetchData, POLLING_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchData, isInitialLoad, isLoggedIn]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        try {
            const response = await fetch(`${API_GATEWAY_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await response.json();
            if (response.ok && data.token) {
                localStorage.setItem('jwt_token', data.token);
                const decodedToken = jwtDecode(data.token);
                setUserRoles(decodedToken.roles || []);
                setIsLoggedIn(true);
            } else {
                throw new Error(data.message || 'Login failed.');
            }
        } catch (error) {
            setLoginError(error.message);
        }
    };
    
    const handleLogout = () => {
        localStorage.removeItem('jwt_token');
        setIsLoggedIn(false);
        setUsername('');
        setPassword('');
        setLoginError('');
        setUserRoles([]);
        window.location.href = HOMEPAGE_URL;
    };

    const handleChartSelectionChange = useCallback(async (chartIndex, newSelectionValue) => {
        const newSelection = JSON.parse(newSelectionValue);
        setCharts(prevCharts => {
            const newCharts = [...prevCharts];
            const isCoil = newSelection.type === 'coil';
            const color = newCharts[chartIndex].data.datasets[0].borderColor;
            newCharts[chartIndex] = {
                selection: newSelection,
                data: createInitialChartState(newSelection.label, color, isCoil)
            };
            return newCharts;
        });
        const historicalData = await fetchInitialChartData(newSelection);
        setCharts(prevCharts => {
            const newCharts = [...prevCharts];
            if (newCharts[chartIndex].selection.label === newSelection.label) {
                newCharts[chartIndex].data.datasets[0].data = historicalData;
            }
            return newCharts;
        });
    }, [fetchInitialChartData]);

    const handleWrite = async (type, address, value) => {
        if (!canWrite()) {
            setError('Access Denied: You do not have permission to perform this action.');
            return;
        }
        const headers = getAuthHeaders();
        if (!headers) { setError('Not authenticated.'); return; }
        if (value === undefined || value === '') { setError('Please enter a valid write value.'); return; }
        
        setError(null);
        try {
            const response = await fetch(`${API_GATEWAY_URL}/api/write/${DASHBOARD_ID}/${type}`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ address, value: Number(value) }),
            });
            const result = await response.json();
            if (!result.success) { throw new Error(result.message || 'Write failed.'); }
            fetchData();
        } catch (err) { setError(`Error: ${err.message}`); }
    };

    const handleHistoryClick = (type, address, name) => setHistoryItem({ type, address, name });
    const handleInputChange = (address, value) => setRegisterInputs(prev => ({ ...prev, [address]: value }));
    const navigateToHomepage = () => window.location.href = HOMEPAGE_URL;

    const openVfdPage = () => {
        const token = localStorage.getItem('jwt_token');
        const urlWithToken = token ? `${VFD_URL}?token=${token}` : VFD_URL;
        window.open(urlWithToken, '_blank');
    };

    const chartOptions = () => { 
        return {
            responsive: true, maintainAspectRatio: false, animation: { duration: 0 },
            plugins: {
                legend: { display: false },
                title: { display: false } 
            },
            scales: {
                x: {
                    ticks: { color: '#CCC', source: 'auto' }, grid: { color: 'rgba(255, 255, 255, 0.1)' }
                },
                y: { ticks: { color: '#CCC' }, grid: { color: 'rgba(255, 255, 255, 0.1)' } }
            }
        };
    };

    const compressorStatusClass = `compressor-status-box ${compressorStatus.toLowerCase().replace(/\s+/g, '-')}`;

    const AppStyles = `
        .App { text-align: center; background-color: #1A1A1A; color: #CCCCCC; } 
        .App-header { background-color: #1A1A1A; min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; font-size: calc(10px + 2vmin); color: #CCCCCC; padding: 20px; }
        
        .header-buttons { display: flex; gap: 10px; }
        .home-button, .alarm-button, .vfd-button { padding: 10px 15px; border: none; border-radius: 5px; cursor: pointer; font-size: 16px; font-weight: bold; }
        .home-button { background-color: #E4002B; color: white; } 
        .alarm-button { background-color: #666666; color: white; } 
        .error { color: #E4002B; margin: 10px; border: 1px solid #E4002B; padding: 10px; border-radius: 5px; background: rgba(228, 0, 43, 0.1); }

        /* --- RESTORED CSS --- */
        /* Changed justify-content back to space-between so the logo shifts to the right */
        .header-top-row { display: flex; justify-content: space-between; align-items: center; width: 100%; margin-bottom: 20px; }

        .te-branding-box { background-color: #2C2C2C; border: 1px solid #444444; border-radius: 5px; padding: 15px 20px; font-size: 1.1rem; font-weight: bold; color: #CCCCCC; text-align: center; }

        /* Ensuring the logo displays correctly */
        .company-logo { max-width: 375px; height: auto; }

        .status-boxes-container { width: 95%; max-width: 1200px; margin: 10px auto 20px; display: flex; flex-direction: column; gap: 10px; }

        .alarm-status-box { padding: 15px; border-radius: 8px; color: white; text-align: center; border: 2px solid; box-shadow: 0 4px 12px rgba(0,0,0,0.4); }
        .alarm-status-box h3 { margin: 0; font-size: 1.2rem; font-weight: bold; text-transform: uppercase; }

        .compressor-status-box { padding: 15px; border-radius: 8px; color: white; text-align: center; border: 2px solid; box-shadow: 0 4px 12px rgba(0,0,0,0.2); font-size: 1.2rem; font-weight: bold; text-transform: uppercase; }

        .alarm-status-box.ok { background-color: #28a745; border-color: #4CAF50; }
        .alarm-status-box.alarm { background-color: #E4002B; border-color: #FF5252; }

        .compressor-status-box.stop { background-color: #444444; border-color: #666666; }
        .compressor-status-box.auto { background-color: #28a745; border-color: #4CAF50; }
        .compressor-status-box.manual { background-color: #FFC107; border-color: #FFD54F; }
        .compressor-status-box.unknown { background-color: #E4002B; border-color: #FF5252; }

        .charts-grid-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 20px; width: 95%; max-width: 1200px; margin: 20px 0; }
        .chart-wrapper { display: flex; flex-direction: column; background: #2C2C2C; padding: 15px; border-radius: 8px; }
        .chart-container { flex-grow: 1; height: 300px; }
        .chart-selector { margin-bottom: 10px; width: 100%; padding: 5px; background-color: #444444; border: 1px solid #666666; color: #CCCCCC; border-radius: 4px; }
        
        .chart-header { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; margin-bottom: 10px; padding: 0 5px; }
        .chart-title { margin: 0; font-size: 1rem; font-weight: bold; color: #CCCCCC; text-align: left; }
        .chart-live-value { font-size: 1.1rem; font-weight: bold; color: #FFFFFF; background-color: #383838; padding: 4px 10px; border-radius: 5px; border: 1px solid #555555; min-width: 60px; text-align: center; }

        .main-content { display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); justify-content: center; width: 95%; max-width: 1200px; gap: 20px; }
        .section-container { background-color: #2C2C2C; padding: 20px; border-radius: 8px; min-width: 300px; }
        .data-container { display: flex; flex-direction: column; gap: 10px; }
        .data-point { display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center; padding: 10px; border: 1px solid #444444; border-radius: 5px; background-color: #383838; }
        .data-point.read-only { grid-template-columns: 1fr auto; }
        .data-point strong { cursor: pointer; text-align: left; }
        .coil-on { border-left: 5px solid #28a745; }
        .coil-off { border-left: 5px solid #E4002B; }
        .status-indicator { font-weight: bold; padding: 5px 10px; border-radius: 5px; }
        .coil-on .status-indicator { background-color: #28a745; color: white; }
        .coil-off .status-indicator { background-color: #E4002B; color: white; }
        .write-container { display: flex; gap: 5px; align-items: center; }
        .write-input { width: 80px; padding: 5px; border-radius: 3px; border: 1px solid #666666; background-color: #444444; color: #CCCCCC; }
        .write-button { padding: 5px 10px; cursor: pointer; border-radius: 3px; border: 1px solid #E4002B; background-color: #1A1A1A; color: #E4002B; }
        
        .unit-controls-container { grid-column: 1 / -1; width: 95%; max-width: 1200px; margin: 20px auto; }

        .collapsible-button { display: flex; justify-content: space-between; align-items: center; width: 100%; background: none; border: none; color: #CCCCCC; cursor: pointer; padding: 0 0 15px 0; border-bottom: 1px solid #444444; margin-bottom: 15px; }
        .collapsible-button h2 { margin: 0; font-size: 1.2rem; font-weight: bold; }
        .collapsible-icon { font-size: 1.2rem; transition: transform 0.2s ease-in-out; }
        .collapsible-content { animation: fadeIn 0.4s ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.75); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background-color: #2C2C2C; padding: 20px; border-radius: 8px; width: 90%; max-width: 1200px; height: 90vh; display: flex; flex-direction: column; position: relative; }
        .modal-close-button { position: absolute; top: 10px; right: 10px; background: #E4002B; color: white; border: none; border-radius: 50%; width: 30px; height: 30px; font-size: 16px; font-weight: bold; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .chart-container-modal { flex-grow: 1; height: 50%; }
        .history-controls { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 15px; align-items: center; }
        .history-controls label { display: flex; align-items: center; gap: 5px; font-size: 14px; }
        .history-controls input, .history-controls button { background-color: #444; border: 1px solid #666; color: #FFF; padding: 5px 10px; border-radius: 4px; }
        .history-table-container { margin-top: 20px; overflow-y: auto; max-height: 35%; }
        .history-table { width: 100%; border-collapse: collapse; }
        .history-table th, .history-table td { border: 1px solid #444; padding: 8px; text-align: left; }
        .history-table th { background-color: #383838; }

        .vfd-page-container { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 1000; background-color: #1A1A1A; display: flex; flex-direction: column; }
        .vfd-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; background-color: #2C2C2C; color: #CCCCCC; flex-shrink: 0; }
        .vfd-header h2 { margin: 0; font-size: 1.5rem; }
        .vfd-iframe { flex-grow: 1; border: none; width: 100%; }

        @media (max-width: 768px) {
            .header-top-row { flex-direction: column; gap: 20px; align-items: center; }
            .te-branding-box { order: 2; }
            .company-logo { max-width: 80%; order: 1; }
            .header-buttons { position: static; width: 100%; margin-top: 20px; justify-content: center; }
            h1 { margin-top: 20px; }
            .home-button, .alarm-button { flex-grow: 1; }
        }
    `;

    const token = localStorage.getItem('jwt_token');
    const vfdUrlWithToken = token ? `${VFD_URL}?token=${token}` : VFD_URL;

    if (!isLoggedIn) {
        return (
            <>
                <style>{AppStyles}</style>
                <div className="login-container">
                    <div className="login-box">
                        <h1 className="login-title">User Login</h1>
                        <p className="login-description">Access the Thomas Evolution Portal</p>
                        <form onSubmit={handleLogin} className="login-form">
                            <div className="input-group">
                                <label htmlFor="username">Username:</label>
                                <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="login-input" />
                            </div>
                            <div className="input-group">
                                <label htmlFor="password">Password:</label>
                                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="login-input" />
                            </div>
                            {loginError && <p className="error-message">{loginError}</p>}
                            <button type="submit" className="login-button">Login</button>
                        </form>
                    </div>
                </div>
            </>
        );
    }

    if (currentPage === 'alarms') return <AlarmPage setCurrentPage={setCurrentPage} />;
    
    if (currentPage === 'vfd') {
        return (
            <>
                <style>{AppStyles}</style>
                <VFDPage setCurrentPage={setCurrentPage} vfdUrlWithToken={vfdUrlWithToken} />
            </>
        );
    }
    
    const alarmStatusClass = alarmStatusMessage.startsWith('🚨') ? 'alarm' : 'ok';

    return (
        <>
            <style>{AppStyles}</style>
           {historyItem && <HistoryModal item={historyItem} onClose={() => setHistoryItem(null)} backendUrl={`${API_GATEWAY_URL}/api/history/${DASHBOARD_ID}`} getAuthHeaders={getAuthHeaders} />}
            <div className="App">                               
                <header className="App-header">
                    <div className="header-top-row">
                        <div className="te-branding-box">Axiom Link built and managed By<br/>Thomas Evolution LLC</div>
                        {/* --- RESTORED LOGO COMPONENT --- */}
                        <img src={AccelLogo} alt="Accel Compression Inc. Logo" className="company-logo" />
                    </div>

                    <h1>SKID 1 DSE-G8680 Dashboard from Thomas Evolution     ..Click On Names For History..</h1>
                    <div className="header-buttons">
                        <button onClick={() => setCurrentPage('alarms')} className="alarm-button">ALARM PAGE</button>
                        <button onClick={openVfdPage} className="vfd-button">VFD CONTROLS</button>
                        <button onClick={navigateToHomepage} className="home-button">Back to Homepage</button>
                    </div>
                    
                    {error && <p className="error">{error}</p>}
                    
                    <div className="status-boxes-container">
                        <div className={`alarm-status-box ${alarmStatusClass}`}>
                            <h3>{alarmStatusMessage}</h3>
                        </div>
                        <div className={compressorStatusClass}>
                            {compressorStatus} Mode
                        </div>
                    </div>
                    
                    <div className="charts-grid-container">
                        {charts.map((chart, index) => {
                            const { type, address, label } = chart.selection;
                            let currentValue = '---'; 
                            
                            if (type === 'coil' && plcData.coils && plcData.coils[address] !== undefined) {
                                currentValue = plcData.coils[address] ? 'ON' : 'OFF';
                            } else if (type === 'register' && plcData.holdingRegisters && plcData.holdingRegisters[address] !== undefined) {
                                const rawValue = plcData.holdingRegisters[address];
                                const scale = chart.selection.scale || 1;
                                const scaledValue = rawValue / scale;
                                // Changed to output 2 decimal places to match DSE
                                currentValue = scale === 1 ? scaledValue : scaledValue.toFixed(2);
                            }

                            return (
                                <div key={index} className="chart-wrapper">
                                    <select className="chart-selector" value={JSON.stringify(chart.selection)} onChange={(e) => handleChartSelectionChange(index, e.target.value)}>
                                        <optgroup label="Analog Inputs">{aiConfig.map(p => <option key={`${p.type}-${p.address}`} value={JSON.stringify(p)}>{p.label}</option>)}</optgroup>
                                        <optgroup label="Analog Outputs">{aoConfig.map(p => <option key={`${p.type}-${p.address}`} value={JSON.stringify(p)}>{p.label}</option>)}</optgroup>
                                        <optgroup label="Digital Inputs">{diConfig.map(p => <option key={`${p.type}-${p.address}`} value={JSON.stringify(p)}>{p.label}</option>)}</optgroup>
                                        <optgroup label="Digital Outputs">{doConfig.map(p => <option key={`${p.type}-${p.address}`} value={JSON.stringify(p)}>{p.label}</option>)}</optgroup>
                                    </select>

                                    <div className="chart-header">
                                        <h3 className="chart-title">{label}</h3>
                                        <span className="chart-live-value">{currentValue}</span>
                                    </div>

                                    <div className='chart-container'>
                                        <Line options={chartOptions()} data={chart.data} />
                                    </div>
                                </div>
                            );
                        })}
                    </div> 
                    
                    <div className="unit-controls-container">
                         <CollapsibleSection title="Unit Controls (Legacy)">
                             <div className="data-container">
                                 {unitControlsConfig.map(({ label, address, isReadOnly }) => {
                                     const value = plcData.coils ? plcData.coils[address] : false;
                                     return (
                                         <div key={`control-${address}`} className={`data-point coil-${value ? 'on' : 'off'}`}>
                                             <strong onClick={() => handleHistoryClick('coil', address, label)}>{label}</strong>
                                             <div className="status-indicator">{value ? 'ON' : 'OFF'}</div>
                                             {!isReadOnly && canWrite() && (
                                                 <button className="write-button" onClick={() => handleWrite('coil', address, value ? 0 : 1)}>Toggle</button>
                                             )}
                                         </div>
                                     );
                                 })}
                             </div>
                         </CollapsibleSection>
                    </div>

                    <div className="main-content">
                        <CollapsibleSection title="Live Digital Inputs (DI)">
                             <div className="data-container">
                                 {diConfig.map(({ label, address }) => {
                                     const value = plcData.coils ? plcData.coils[address] : false;
                                     return (
                                         <div key={`di-${address}`} className={`data-point read-only coil-${value ? 'on' : 'off'}`}>
                                             <strong onClick={() => handleHistoryClick('coil', address, label)}>{label}</strong>
                                             <div className="status-indicator">{value ? 'ON' : 'OFF'}</div>
                                         </div>
                                     );
                                 })}
                             </div>
                        </CollapsibleSection>
                        
                        <CollapsibleSection title="Live Digital Outputs (DO)">
                             <div className="data-container">
                                 {doConfig.map(({ label, address, isReadOnly }) => {
                                     const value = plcData.coils ? plcData.coils[address] : false;
                                     return (
                                         <div key={`do-${address}`} className={`data-point coil-${value ? 'on' : 'off'}`}>
                                             <strong onClick={() => handleHistoryClick('coil', address, label)}>{label}</strong>
                                             <div className="status-indicator">{value ? 'ON' : 'OFF'}</div>
                                             {!isReadOnly && canWrite() && (
                                                 <button className="write-button" onClick={() => handleWrite('coil', address, value ? 0 : 1)}>Toggle</button>
                                             )}
                                         </div>
                                     );
                                 })}
                             </div>
                        </CollapsibleSection>

                        <div className="section-container">
                            <h2>Live DSE G8680 Telemetry (AI)</h2>
                            <div className="data-container">
                                {aiConfig.map(({ label, address, scale = 1 }) => {
                                    const rawValue = plcData.holdingRegisters ? plcData.holdingRegisters[address] : undefined;
                                    let displayValue = '---';
                                    if (rawValue !== undefined) {
                                        const scaledValue = rawValue / scale;
                                        // Changed to output 2 decimal places to match DSE
                                        displayValue = scale === 1 ? scaledValue : scaledValue.toFixed(2);
                                    }

                                    return (
                                        <div key={`ai-${address}`} className={`data-point read-only hr-value`}>
                                            <strong onClick={() => handleHistoryClick('register', address, label)}>{label}</strong>
                                            <div className="register-value-display">Current: {displayValue}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="section-container">
                            <h2>DSE Control Commands (AO)</h2>
                            <div className="data-container">
                                {aoConfig.map(({ label, address, isReadOnly }) => {
                                    const value = plcData.holdingRegisters ? plcData.holdingRegisters[address] : 0;
                                    return (
                                        <div key={`ao-${address}`} className={`data-point hr-value`}>
                                            <strong onClick={() => handleHistoryClick('register', address, label)}>{label}</strong>
                                            <div className="register-value-display">Current: {value}</div>
                                            {!isReadOnly && canWrite() && (
                                                <div className="write-container">
                                                    <input type="number" className="write-input" placeholder="Command Key" value={registerInputs[address] || ''} onChange={(e) => handleInputChange(address, e.target.value)} />
                                                    <button className="write-button" onClick={() => handleWrite('register', address, registerInputs[address])}>Send Key</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </header>
            </div>
        </>
    );
}

export default App;